import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { MapPin, ArrowUpRight } from "lucide-react";

export default function ContactV2() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", project: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry received. Our team will call within one working day.");
      setForm({ name: "", email: "", phone: "", project: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact-v2"
      data-testid="contact-v2"
      className="relative bg-charcoal text-charcoal_ink py-24 md:py-32 px-6 md:px-10 overflow-hidden v2-grain"
    >
      <div className="max-w-[1400px] mx-auto relative">
        <div className="chapter-num mb-6 flex items-center">
          <span className="inline-block w-8 h-px bg-glow_lime glow-box align-middle mr-3" />
          <span className="glow-lime-soft text-glow_lime">ENQUIRE · FINAL CHAPTER</span>
        </div>
        <motion.h2
          initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="font-serif-display text-5xl md:text-7xl lg:text-[104px] leading-[0.95] tracking-tight max-w-5xl text-charcoal_ink"
        >
          Begin the <span className="italic glow-lime">conversation.</span>
        </motion.h2>

        <div className="grid grid-cols-12 gap-8 md:gap-16 mt-16 md:mt-24">
          {/* Left — Contact details */}
          <div className="col-span-12 md:col-span-4 space-y-10">
            <div>
              <div className="chapter-num text-charcoal_ink/50 mb-3">STUDIO</div>
              <p className="text-charcoal_ink/85 text-base leading-relaxed">
                MGM Heights, Plot no- 140/9957,<br />
                K living · 3rd Floor, Kalinga Nagar,<br />
                Bhubaneswar, Khurdha — 751003,<br />
                Odisha, India.
              </p>
            </div>
            <div>
              <div className="chapter-num text-charcoal_ink/50 mb-3">DIRECT</div>
              <div>
                <a href="tel:08908101907" className="link-underline text-charcoal_ink text-lg hover:text-glow_lime transition-colors">
                  089081 01907
                </a>
              </div>
              <div className="mt-2">
                <a href="mailto:studio@kliving.in" className="link-underline text-charcoal_ink text-lg hover:text-glow_lime transition-colors">
                  studio@kliving.in
                </a>
              </div>
            </div>
            <div>
              <div className="chapter-num text-charcoal_ink/50 mb-3">HOURS</div>
              <p className="text-charcoal_ink/85 text-base">Mon — Sat · 10:00 to 19:00 IST</p>
            </div>
            <div className="flex items-center gap-4 text-charcoal_ink/60 chapter-num">
              <MapPin className="w-4 h-4 text-glow_lime glow-icon" /> 7Q77+QPR BHUBANESWAR
            </div>
          </div>

          {/* Right — Form */}
          <form
            onSubmit={submit}
            data-testid="contact-v2-form"
            className="col-span-12 md:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-10"
          >
            <Field label="Full name" testid="contact-v2-name" value={form.name} onChange={(v) => update("name", v)} required />
            <Field label="Email address" testid="contact-v2-email" type="email" value={form.email} onChange={(v) => update("email", v)} required />
            <Field label="Phone number" testid="contact-v2-phone" value={form.phone} onChange={(v) => update("phone", v)} required />
            <Field label="Project of interest" testid="contact-v2-project" value={form.project} onChange={(v) => update("project", v)} placeholder="Azure · Plaza · Meridian" />
            <div className="md:col-span-2">
              <Field label="Message" testid="contact-v2-message" textarea value={form.message} onChange={(v) => update("message", v)} required />
            </div>
            <div className="md:col-span-2 flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
              <p className="text-charcoal_ink/50 text-xs max-w-sm">
                By submitting, you agree to be contacted by KLiving. We never share your details with third parties.
              </p>
              <button
                type="submit"
                data-testid="contact-v2-submit"
                disabled={submitting}
                className="btn-kl-v2 btn-kl-v2-solid disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send Enquiry"} <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, value, onChange, testid, type = "text", required, placeholder, textarea }) {
  const common =
    "w-full bg-transparent border-0 border-b border-charcoal_ink/20 focus:border-glow_lime focus:outline-none py-3 text-charcoal_ink text-lg placeholder:text-charcoal_ink/30 transition-colors duration-300";
  return (
    <label className="block">
      <span className="chapter-num text-charcoal_ink/50 block mb-2">{label.toUpperCase()}{required ? " *" : ""}</span>
      {textarea ? (
        <textarea
          data-testid={testid}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          required={required}
          placeholder={placeholder}
          className={common + " resize-none"}
        />
      ) : (
        <input
          data-testid={testid}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          placeholder={placeholder}
          className={common}
        />
      )}
    </label>
  );
}
